/**
 * useModules — exposes which modules are enabled for the current user's company.
 *
 * Behaviour:
 *   - Pulls the modules array off the auth user (populated by /auth/me).
 *   - Platform owners always have every module.
 *   - If `modules` is missing/undefined (legacy /auth/me payload), defaults to "all enabled"
 *     so nothing breaks until the user re-logs.
 *   - 'core' is always considered enabled.
 */
import { useAuth } from '../context/AuthContext';
import { ALL_MODULE_CODES } from './catalog';

export function useModules() {
  const { user } = useAuth();

  const isPlatformOwner = !!user?.isSuperAdmin;
  let enabled = user?.modules;
  if (!Array.isArray(enabled) || enabled.length === 0) {
    // Legacy fallback — assume everything until /auth/me populates the field
    enabled = ALL_MODULE_CODES;
  }
  if (isPlatformOwner) enabled = ALL_MODULE_CODES;
  if (!enabled.includes('core')) enabled = ['core', ...enabled];

  const enabledSet = new Set(enabled);

  const trials = Array.isArray(user?.moduleTrials) ? user.moduleTrials : [];
  const trialSet = new Set(trials.map(t => t.code));

  return {
    enabled,
    isEnabled: (code) => code === 'core' || enabledSet.has(code),
    hasModule: (code) => code === 'core' || enabledSet.has(code),
    isPlatformOwner,
    trials,
    hasActiveTrial: (code) => trialSet.has(code),
  };
}

export default useModules;
